import { useEffect } from "react";
import { useTranslation } from "../i18n/useTranslation";

interface NumberPadProps {
  onDigit: (digit: number) => void;
  onBackspace: () => void;
  onSubmit: () => void;
  submitDisabled?: boolean;
}

const KEY_ROWS = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9],
] as const;

export function NumberPad({
  onDigit,
  onBackspace,
  onSubmit,
  submitDisabled = false,
}: NumberPadProps) {
  const { t } = useTranslation();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key >= "0" && e.key <= "9") onDigit(parseInt(e.key, 10));
      else if (e.key === "Backspace") onBackspace();
      else if (e.key === "Enter" && !submitDisabled) onSubmit();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onDigit, onBackspace, onSubmit, submitDisabled]);

  const baseBtn =
    "h-14 rounded-xl font-mono text-xl font-medium border transition-all duration-100 active:scale-95";
  const numBtn = `${baseBtn} bg-bg-2 border-[var(--color-line)] hover:border-[var(--color-line-strong)] text-fg-0`;

  return (
    <div className="max-w-xs mx-auto w-full flex flex-col gap-2">
      {KEY_ROWS.map((row, ri) => (
        <div key={ri} className="grid grid-cols-3 gap-2">
          {row.map((n) => (
            <button key={n} type="button" className={numBtn} onClick={() => onDigit(n)}>
              {n}
            </button>
          ))}
        </div>
      ))}
      <div className="grid grid-cols-3 gap-2">
        <button
          type="button"
          className={`${baseBtn} bg-bg-2 border-[var(--color-line)] hover:border-[var(--color-line-strong)] text-fg-2 text-sm`}
          onClick={onBackspace}
          aria-label={t("common.backspace")}
        >
          ⌫
        </button>
        <button type="button" className={numBtn} onClick={() => onDigit(0)}>
          0
        </button>
        <button
          type="button"
          disabled={submitDisabled}
          className={[
            baseBtn,
            "text-sm font-semibold",
            submitDisabled
              ? "bg-bg-2 border-[var(--color-line)] text-fg-3 opacity-50 cursor-not-allowed"
              : "bg-cyan border-cyan text-bg-1 hover:opacity-90",
          ].join(" ")}
          onClick={onSubmit}
        >
          {t("common.enter")}
        </button>
      </div>
    </div>
  );
}
